/**
 * Contact Management API Service
 * Handles Customer and Supplier CRUD operations with proper error handling
 */

import config from "../../config";
import { apiFetch, createMultiConsumerGet } from "./apiFetch";


const getAccountId = () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return user.accountId;
};

const buildUrl = (path, params = {}) => {
    const filtered = Object.fromEntries(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== "")
    );
    const qs = new URLSearchParams({ account_id: getAccountId(), ...filtered });
    return `${config.apiUrl}/${path}?${qs}`;
};

// Error with the server's own reason (e.g. "Customer not found") when it sends
// one, otherwise "HTTP 404". `status` lets screens react to a specific code.
const responseError = async (res) => {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || body.message || `HTTP ${res.status}`);
    err.status = res.status;
    return { err, body };
};

const sharedGet = createMultiConsumerGet(async (url, options) => {
    const res = await apiFetch(url, options);
    if (!res.ok) throw (await responseError(res)).err;
    return res.json();
});
const get = (path, params = {}, options = {}) => {
    const url = buildUrl(path, params);
    return sharedGet(url, options);
};

// ════════════════════════════════════════════════════════════
// CUSTOMERS API
// ════════════════════════════════════════════════════════════

export const getCustomers = (p = {}, options = {}) =>
    get("getCustomers", p, options);

export const getCustomerKpis = () => get("getCustomerKpis");

export const getCustomerById = (id, options = {}) => get("getCustomerById", { id }, options);

// Every active company name, sorted A–Z, for the Company Name dropdown on
// Sales/Finance documents. No 1,000-row limit: the server returns names only.
export const getActiveCustomerCompanyNames = (options = {}) =>
    get("getActiveCustomerCompanyNames", {}, options);

// Shared lookup by company name for the document screens (customers on
// Sales/Finance documents, suppliers on Purchasing/Finance documents).
// Results are remembered for 1 minute so picking a company doesn't ask the server
// again on every change, and are cleared whenever a record is added, edited or
// deleted — so contact persons and addresses never stay out of date.
const LOOKUP_TTL_MS = 60 * 1000;

const createNameLookup = (fetchList) => {
    let cache = {};
    const clear = () => { cache = {}; };
    const lookup = async (companyName) => {
        if (!companyName) return [];
        // Key includes the account so a different login on the same browser never sees old data
        const key = `${getAccountId()}|${companyName}`;
        const cached = cache[key];
        if (cached && Date.now() - cached.time < LOOKUP_TTL_MS) return cached.data;

        const res = await fetchList({ pageSize: 1000, page: 1, search: companyName, searchType: "name" });
        const data = res?.data || [];
        cache[key] = { data, time: Date.now() };
        return data;
    };
    return { lookup, clear };
};

const customerLookup = createNameLookup((p) => getCustomers(p));
export const getCustomersByName = customerLookup.lookup;
export const clearCustomerLookupCache = customerLookup.clear;

// Every company name (Active and Inactive), sorted A–Z, for the Customer form's
// Company Name suggestions. Names only, so there's no 1,000-row limit.
export const getCustomerCompanyNames = (options = {}) =>
    get("getCustomerCompanyNames", {}, options);

// 400 = bad data, 409 = duplicate or a name that's locked by documents. Besides
// the readable `error` the server sends `fields` ({ fieldName: message }) so
// the form can highlight them.
const contactSaveError = async (res) => {
    const { err, body } = await responseError(res);
    if (body.fields) err.fields = body.fields;
    return err;
};

// 409 = the record is used in documents; the server explains why in `error`
const contactDeleteError = async (res) => {
    const { err } = await responseError(res);
    if (res.status === 409) err.userMessage = err.message;
    return err;
};

export const createCustomer = async (data) => {
    const res = await apiFetch(`${config.apiUrl}/createCustomer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: getAccountId(), ...data }),
    });
    if (!res.ok) throw await contactSaveError(res);
    clearCustomerLookupCache();
    return res.json();
};

export const updateCustomer = async (id, data) => {
    const res = await apiFetch(`${config.apiUrl}/updateCustomer`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: getAccountId(), id, ...data }),
    });
    if (!res.ok) throw await contactSaveError(res);
    clearCustomerLookupCache();
    return res.json();
};

export const deleteCustomer = async (id) => {
    const res = await apiFetch(buildUrl("deleteCustomer", { id }), { method: "DELETE" });
    if (!res.ok) throw await contactDeleteError(res);
    clearCustomerLookupCache();
    return res.json();
};

// ════════════════════════════════════════════════════════════
// SUPPLIERS API
// ════════════════════════════════════════════════════════════

export const getSuppliers = (p = {}, options = {}) =>
    get("getSuppliers", p, options);

export const getSupplierKpis = () => get("getSupplierKpis");

export const getSupplierById = (id, options = {}) => get("getSupplierById", { id }, options);

// Every company name (Active and Inactive), sorted A–Z, for the Supplier form's
// Company Name suggestions. Names only, so there's no 1,000-row limit.
export const getSupplierCompanyNames = (options = {}) =>
    get("getSupplierCompanyNames", {}, options);

// Every active company name, sorted A–Z, for the Company Name dropdown on
// Purchasing/Finance documents. No 1,000-row limit: the server returns names only.
export const getActiveSupplierCompanyNames = (options = {}) =>
    get("getActiveSupplierCompanyNames", {}, options);

// Shared supplier lookup by company name (see createNameLookup above)
const supplierLookup = createNameLookup((p) => getSuppliers(p));
export const getSuppliersByName = supplierLookup.lookup;
export const clearSupplierLookupCache = supplierLookup.clear;

export const createSupplier = async (data) => {
    const res = await apiFetch(`${config.apiUrl}/createSupplier`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: getAccountId(), ...data }),
    });
    if (!res.ok) throw await contactSaveError(res);
    clearSupplierLookupCache();
    return res.json();
};

export const updateSupplier = async (id, data) => {
    const res = await apiFetch(`${config.apiUrl}/updateSupplier`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ account_id: getAccountId(), id, ...data }),
    });
    if (!res.ok) throw await contactSaveError(res);
    clearSupplierLookupCache();
    return res.json();
};

export const deleteSupplier = async (id) => {
    const res = await apiFetch(buildUrl("deleteSupplier", { id }), { method: "DELETE" });
    if (!res.ok) throw await contactDeleteError(res);
    clearSupplierLookupCache();
    return res.json();
};

// ════════════════════════════════════════════════════════════
// CONSTANTS
// ════════════════════════════════════════════════════════════

export const INCOTERMS_LIST = [
    "EXW – Ex Works",
    "FCA – Free Carrier",
    "FAS – Free Alongside Ship",
    "FOB – Free On Board",
    "CFR – Cost and Freight",
    "CIF – Cost, Insurance and Freight",
    "CPT – Carriage Paid To",
    "CIP – Carriage and Insurance Paid To",
    "DAP – Delivered At Place",
    "DPU – Delivered at Place Unloaded",
    "DDP – Delivered Duty Paid",
];

export const COMMODITY_LIST = [
    "Aluminium", "Steel", "Copper", "Plastics", "ABS Resin",
    "PCB", "Electronics", "Cables", "Rubber", "Gaskets",
    "Bearings", "Fasteners", "Paint", "Chemicals", "Solvents",
    "Timber", "Glass", "Packaging", "Cartons", "Lubricants",
    "Sensors", "Valves", "Adhesives", "Welding Gas", "Logistics",
];

export const STATUSES = ["All", "Active", "Inactive"];
